import { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Stack,
    Paper,
    IconButton,
    Tooltip,
    MenuItem,
    Chip,
    Divider,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';

// Curated frequency list — common primary-care prescribing patterns
// written in plain English so the visit summary reads naturally rather
// than using Latin abbreviations (BID/TID/QID/etc) that vary by region.
const FREQUENCY_OPTIONS = [
    'Once daily',
    'Twice daily',
    'Three times daily',
    'Four times daily',
    'Every 4 hours',
    'Every 6 hours',
    'Every 8 hours',
    'Every 12 hours',
    'At bedtime',
    'As needed (PRN)',
    'Stat (one dose)',
    'Weekly',
];

const DURATION_UNITS = ['days', 'weeks', 'months'];

// Combine the structured duration inputs into a single string the
// encounter schema and the PDF report already understand
// (e.g. "7 days", "2 weeks"). Empty value → empty string.
function composeDuration(value, unit) {
    const v = String(value || '').trim();
    if (!v) return '';
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return '';
    const u = unit || 'days';
    // Singular form when value === 1 reads more naturally on the PDF.
    if (n === 1) return `1 ${u.replace(/s$/, '')}`;
    return `${n} ${u}`;
}

export default function TreatmentTab({ encounter, onPatch, disabled }) {
    // Free-text medication entry — the master-list autocomplete was removed
    // because Guyana-side pharmacies stock medications outside the seeded
    // SNOMED/RxNorm sets and clinicians needed to type whatever they wanted.
    const [name, setName] = useState('');
    const [dose, setDose] = useState('');
    const [route, setRoute] = useState('');
    const [frequency, setFrequency] = useState('');
    const [durationValue, setDurationValue] = useState('');
    const [durationUnit, setDurationUnit] = useState('days');

    const items = encounter?.treatment || [];
    const none = Boolean(encounter?.treatmentNone);
    const procedureNotes = encounter?.procedureNotes || '';

    const canAdd = name.trim().length > 0;
    const add = () => {
        if (!canAdd) return;
        onPatch({
            // Prescribing supersedes any "no treatment required" assertion.
            treatmentNone: false,
            treatment: [
                ...items,
                {
                    name: name.trim(),
                    dose,
                    route,
                    frequency,
                    duration: composeDuration(durationValue, durationUnit),
                },
            ],
        });
        setName(''); setDose(''); setRoute(''); setFrequency('');
        setDurationValue(''); setDurationUnit('days');
    };
    const removeAt = (idx) => onPatch({ treatment: items.filter((_, i) => i !== idx) });

    return (
        <Box>
            <SectionHeading
                title="Treatment & prescriptions"
                hint="Each line is one medication with its dose, route, frequency, and duration."
            />

            {!disabled && items.length === 0 && (
                <Box sx={{ mb: 2 }}>
                    <Chip
                        clickable
                        onClick={() => onPatch({ treatmentNone: !none })}
                        icon={none ? <CheckRoundedIcon /> : undefined}
                        label="No treatment required"
                        variant={none ? 'filled' : 'outlined'}
                        sx={none ? {
                            bgcolor: 'rgba(46,125,50,0.12)',
                            color: 'success.dark',
                            border: '1px solid rgba(46,125,50,0.4)',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'success.dark' },
                        } : {
                            borderColor: 'rgba(5,47,93,0.30)',
                            color: NAVY,
                            fontWeight: 600,
                        }}
                    />
                </Box>
            )}

            {!disabled && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: 'rgba(5,47,93,0.10)' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>
                        Prescribe medication
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ md: 'flex-end' }} flexWrap="wrap">
                        <TextField
                            label="Medication"
                            size="small"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                            placeholder="e.g. Amoxicillin 500 mg"
                            sx={{ flex: '2 1 220px', minWidth: { xs: '100%', md: 220 } }}
                        />
                        <TextField label="Dose" size="small" value={dose} onChange={(e) => setDose(e.target.value)} sx={{ width: { xs: '100%', md: 110 } }} />
                        <TextField label="Route" size="small" value={route} onChange={(e) => setRoute(e.target.value)} sx={{ width: { xs: '100%', md: 110 } }} />
                        <TextField
                            label="Frequency"
                            size="small"
                            select
                            value={frequency}
                            onChange={(e) => setFrequency(e.target.value)}
                            sx={{ width: { xs: '100%', md: 180 } }}
                            SelectProps={{ displayEmpty: true }}
                            // displayEmpty renders "Not specified" while the value is still "",
                            // so the label must be pinned shrunk or it overlaps that text.
                            InputLabelProps={{ shrink: true }}
                        >
                            <MenuItem value=""><em>Not specified</em></MenuItem>
                            {FREQUENCY_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Duration"
                            size="small"
                            type="number"
                            value={durationValue}
                            onChange={(e) => setDurationValue(e.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                            sx={{ width: { xs: '100%', md: 100 } }}
                        />
                        <TextField
                            label="Unit"
                            size="small"
                            select
                            value={durationUnit}
                            onChange={(e) => setDurationUnit(e.target.value)}
                            sx={{ width: { xs: '100%', md: 110 } }}
                        >
                            {DURATION_UNITS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                        <Button
                            onClick={add}
                            disabled={!canAdd}
                            variant="contained"
                            startIcon={<AddRoundedIcon />}
                            sx={{
                                bgcolor: NAVY,
                                boxShadow: 'none',
                                textTransform: 'none',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                            }}
                        >
                            Add
                        </Button>
                    </Stack>
                </Paper>
            )}

            {items.length === 0 ? (
                <Box
                    sx={{
                        py: 3,
                        px: 2,
                        borderRadius: 1.5,
                        bgcolor: none ? 'rgba(46,125,50,0.06)' : 'rgba(5,47,93,0.025)',
                        border: `1px dashed ${none ? 'rgba(46,125,50,0.35)' : 'rgba(5,47,93,0.15)'}`,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body2" sx={{ color: none ? 'success.dark' : 'text.secondary' }}>
                        {none
                            ? 'Recorded: no treatment required. This will appear on the visit summary.'
                            : 'No prescriptions yet.'}
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={1}>
                    {items.map((t, i) => (
                        <Paper
                            key={i}
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                borderRadius: 1.5,
                                borderColor: 'rgba(5,47,93,0.10)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <MedicationOutlinedIcon sx={{ color: NAVY, fontSize: 22 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
                                    {t.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {[t.dose, t.route, t.frequency, t.duration].filter(Boolean).join(' · ') || '—'}
                                </Typography>
                            </Box>
                            {!disabled && (
                                <Tooltip title="Remove" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => removeAt(i)}
                                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                    >
                                        <DeleteOutlineRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Paper>
                    ))}
                </Stack>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 0.6, fontWeight: 700, fontSize: 10 }}>
                Procedures performed
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Note any procedures done at this visit — e.g. incision &amp; drainage of an abscess, or suturing a
                wound. This appears under Treatment on the patient summary.
            </Typography>
            <TextField
                fullWidth
                multiline
                minRows={3}
                value={procedureNotes}
                onChange={(e) => onPatch({ procedureNotes: e.target.value })}
                disabled={disabled}
                placeholder="e.g. Incision and drainage of left forearm abscess; wound irrigated and packed."
                InputProps={{
                    sx: {
                        fontSize: 14.5,
                        lineHeight: 1.55,
                        bgcolor: disabled ? 'action.hover' : '#fff',
                        '& fieldset': { borderColor: 'rgba(5,47,93,0.15)' },
                        '&:hover fieldset': { borderColor: 'rgba(5,47,93,0.35) !important' },
                        '&.Mui-focused fieldset': { borderColor: '#052F5D !important', borderWidth: 1 },
                    },
                }}
            />
        </Box>
    );
}
